import type { CatalogueListing } from "../../api/catalogue";
import { formatPesos } from "../../lib/money";
import { Badge } from "../ui";
import { cx } from "../ui/cx";

/** "/ pc" or "/ m": what one of the price buys. Items sold by length are sold in whole metres. */
export const unitLabel = (unit: CatalogueListing["unit"]) => (unit === "metre" ? "/ m" : "/ pc");

const PRICE_SIZE = { sm: "[font-size:var(--text-base)]", md: "[font-size:var(--text-lg)]", lg: "[font-size:var(--text-3xl)]" } as const;

/**
 * One price, already computed for the viewer by the server's pricing service. There are no
 * tiers to show or hint at: a price nobody here is entitled to is never rendered (design doc §2.7).
 */
export function PriceTag({ cents, unit, size = "md", className }: { cents: number; unit: CatalogueListing["unit"]; size?: keyof typeof PRICE_SIZE; className?: string }) {
  return (
    <span className={cx("inline-flex items-baseline gap-2", className)}>
      <span className={cx("type-price whitespace-nowrap text-text-strong", PRICE_SIZE[size])}>{formatPesos(cents)}</span>
      <span className="type-small text-text-muted">{unitLabel(unit)}</span>
    </span>
  );
}

/** Colour plus a word. Out of stock still shows its price (PRD 5.7). */
export function StockBadge({ inStock }: { inStock: boolean }) {
  return <Badge tone={inStock ? "stock" : "out"}>{inStock ? "In stock" : "Out of stock"}</Badge>;
}

/** Genuine or aftermarket, when we've confirmed which. Unconfirmed says nothing rather than guess. */
export function OriginBadge({ origin }: { origin: CatalogueListing["origin"] }) {
  if (!origin) return null;
  return <Badge tone={origin === "genuine" ? "genuine" : "machine"}>{origin === "genuine" ? "Genuine" : "Aftermarket"}</Badge>;
}

/** The number printed on the diagram and in the parts list. Gold when selected; grey for an unnumbered row. */
export function PosChip({ label, active, size = "md" }: { label: string | null; active?: boolean; size?: "md" | "lg" }) {
  return (
    <span
      className={cx(
        "inline-flex flex-none items-center justify-center rounded-full border-2 px-1 type-sku whitespace-nowrap",
        size === "lg" ? "h-8 min-w-9 [font-size:var(--text-sm)]" : "h-6 min-w-[26px] [font-size:var(--text-xs)]",
        active ? "border-gold-500 bg-gold-500 text-ink-900" : label ? "border-green-700 bg-white text-green-800" : "border-ink-200 bg-white text-ink-500",
      )}
    >
      {label ?? "—"}
    </span>
  );
}

/** The number a customer reads off the part: the manufacturer's, or the standard it's made to; else our SKU. */
export function ReferenceNumber({ listing, className }: { listing: Pick<CatalogueListing, "reference_number" | "reference_kind" | "sku">; className?: string }) {
  return (
    <span className={cx("block min-w-0", className)}>
      <span className="type-sku [overflow-wrap:anywhere]">{listing.reference_number ?? listing.sku}</span>
      {listing.reference_kind === "standard" && (
        <span className="mt-0.5 block type-eyebrow tracking-[var(--tracking-wider)] text-ink-500 uppercase">Standard</span>
      )}
    </span>
  );
}
